import { useMemo } from 'react';
import { useRouterState } from '@tanstack/react-router';

export interface EntityRoutePathState {
  selectedId?: string;
  isIndexRoute: boolean;
  isNewRoute: boolean;
  isEditRoute: boolean;
  isFormMode: boolean;
  hasChildRoute: boolean;
  childSegments: string[];
}

interface UseEntityRouteStateOptions<TSearch, TPreservedSearch = TSearch> {
  basePath: string;
  search: TSearch;
  cleanSearch?: (search: TSearch) => TPreservedSearch;
  /** Additional second-level segments (after the entity id) that should be treated as form mode. */
  formSegments?: string[];
}

function cloneSearch<TSearch>(search: TSearch): TSearch {
  if (!search || typeof search !== 'object') return search;
  return { ...search };
}

export function resolveEntityRoutePathState(
  pathname: string,
  basePath: string,
): EntityRoutePathState {
  const normalizedBase = basePath.replace(/\/+$/, '');
  const normalizedPath = pathname.replace(/\/+$/, '');

  if (normalizedPath !== normalizedBase && !normalizedPath.startsWith(`${normalizedBase}/`)) {
    return {
      isIndexRoute: false,
      isNewRoute: false,
      isEditRoute: false,
      isFormMode: false,
      hasChildRoute: false,
      childSegments: [],
    };
  }

  const childPath = normalizedPath.slice(normalizedBase.length).replace(/^\/+/, '');
  const childSegments = childPath ? childPath.split('/').filter(Boolean) : [];
  const selectedId = childSegments[0] && childSegments[0] !== 'new' ? childSegments[0] : undefined;
  const isIndexRoute = childSegments.length === 0;
  const isNewRoute = childSegments[0] === 'new';
  const isEditRoute = Boolean(selectedId && childSegments[1] === 'edit');
  const isNestedNewRoute = Boolean(selectedId && childSegments[2] === 'new');
  const isNestedEditRoute = Boolean(selectedId && childSegments[3] === 'edit');
  const isFormMode = isNewRoute || isEditRoute || isNestedNewRoute || isNestedEditRoute;

  return {
    selectedId,
    isIndexRoute,
    isNewRoute,
    isEditRoute,
    isFormMode,
    hasChildRoute: childSegments.length > 0,
    childSegments,
  };
}

export function useEntityRouteState<TSearch, TPreservedSearch = TSearch>({
  basePath,
  search,
  cleanSearch,
  formSegments,
}: UseEntityRouteStateOptions<TSearch, TPreservedSearch>) {
  const { location } = useRouterState();
  const routeState = useMemo(() => {
    const state = resolveEntityRoutePathState(location.pathname, basePath);
    if (!state.isFormMode && formSegments?.length && state.selectedId) {
      const secondSegment = state.childSegments[1];
      if (secondSegment && formSegments.includes(secondSegment)) {
        return { ...state, isFormMode: true };
      }
    }
    return state;
  }, [basePath, formSegments, location.pathname]);
  const preservedSearch = useMemo(
    () => (cleanSearch ? cleanSearch(cloneSearch(search)) : search),
    [cleanSearch, search],
  );

  return {
    ...routeState,
    preservedSearch,
  };
}
